import React from 'react';
import { SocialLink } from '../types';
import '../styles/Footer.css';

const Footer: React.FC = () => {
  const socialLinks: SocialLink[] = [
    {
      platform: 'GitHub',
      url: 'https://github.com/GriffinCanCode',
      icon: '💻',
      label: 'View my code on GitHub'
    },
    {
      platform: 'LinkedIn',
      url: 'https://www.linkedin.com/in/griffin-strier-9ab9b0180/',
      icon: '💼',
      label: 'Connect on LinkedIn'
    },
    {
      platform: 'Email',
      url: 'mailto:griffin@griffin-code.com',
      icon: '📧',
      label: 'Send me an email'
    }
  ];

  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Newsletter subscription handler (HTML5 form validation)
  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    
    if (email) {
      // Store subscription preference with Web Storage
      if ('localStorage' in window) {
        localStorage.setItem('griffin-newsletter-email', email);
        localStorage.setItem('griffin-newsletter-date', new Date().toISOString());
      }
      
      // Show success message (in real app, would send to API)
      console.log('Newsletter subscription:', email);
      alert('Thanks for subscribing! (Demo mode)');
      e.currentTarget.reset();
    }
  };

  return (
    <footer 
      className="footer"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="container">
        <div className="footer__content">
          {/* Main footer content with semantic sections */}
          <div className="footer__main">
            {/* Brand section with semantic markup */}
            <section className="footer__brand" aria-labelledby="footer-brand-title">
              <h3 id="footer-brand-title" className="footer__logo">Griffin's World</h3>
              <p className="footer__tagline">
                Building the future, one line of code at a time.
              </p>
              
              {/* Newsletter subscription with HTML5 form features */}
              <form 
                className="footer__newsletter" 
                onSubmit={handleNewsletterSubmit}
                aria-labelledby="newsletter-title"
              >
                <h4 id="newsletter-title" className="footer__newsletter-title">
                  Stay Updated
                </h4>
                <div className="footer__newsletter-form">
                  <label htmlFor="newsletter-email" className="visually-hidden">
                    Email address for newsletter
                  </label>
                  <input
                    type="email"
                    id="newsletter-email"
                    name="email"
                    placeholder="Enter your email"
                    required
                    aria-describedby="newsletter-description"
                    className="footer__newsletter-input"
                    autoComplete="email"
                  />
                  <button 
                    type="submit" 
                    className="footer__newsletter-button"
                    aria-label="Subscribe to newsletter"
                  >
                    Subscribe
                  </button>
                </div>
                <p id="newsletter-description" className="footer__newsletter-description">
                  Get notified about new projects and articles. No spam, unsubscribe anytime.
                </p>
              </form>
            </section>

            {/* Navigation links with semantic structure */}
            <nav className="footer__links" aria-labelledby="footer-nav-title">
              <h4 id="footer-nav-title" className="visually-hidden">Footer navigation</h4>
              
              <section className="footer__column" aria-labelledby="quick-links-title">
                <h4 id="quick-links-title">Quick Links</h4>
                <ul role="list">
                  <li><a href="#home" aria-label="Navigate to home section">Home</a></li>
                  <li><a href="#about" aria-label="Navigate to about section">About</a></li>
                  <li><a href="#skills" aria-label="Navigate to skills section">Skills</a></li>
                  <li><a href="#projects" aria-label="Navigate to projects section">Projects</a></li>
                  <li><a href="#contact" aria-label="Navigate to contact section">Contact</a></li>
                </ul>
              </section>

              <section className="footer__column" aria-labelledby="services-title">
                <h4 id="services-title">Services</h4>
                <ul role="list">
                  <li><a href="#contact" aria-label="Learn about web development services">Web Development</a></li>
                  <li><a href="#contact" aria-label="Learn about mobile app services">Mobile Apps</a></li>
                  <li><a href="#contact" aria-label="Learn about UI/UX design services">UI/UX Design</a></li>
                  <li><a href="#contact" aria-label="Learn about consulting services">Consulting</a></li>
                  <li><a href="#contact" aria-label="Learn about technical writing services">Technical Writing</a></li>
                </ul>
              </section>

              <section className="footer__column" aria-labelledby="technologies-title">
                <h4 id="technologies-title">Technologies</h4>
                <ul role="list" aria-label="Technologies I work with">
                  <li>React & Next.js</li>
                  <li>TypeScript</li>
                  <li>Node.js & Python</li>
                  <li>AWS Cloud</li>
                  <li>Mobile Development</li>
                </ul>
              </section>
            </nav>
          </div>

          {/* Social links section with enhanced accessibility */}
          <section className="footer__social" aria-labelledby="social-title">
            <h4 id="social-title">Let's Connect</h4>
            <nav className="footer__social-links" aria-label="Social media links">
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  className="footer__social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  title={link.label}
                >
                  <span className="footer__social-icon" aria-hidden="true">{link.icon}</span>
                  <span className="footer__social-text">{link.platform}</span>
                </a>
              ))}
            </nav>
          </section>
        </div>

        {/* Footer bottom with legal and utility links */}
        <div className="footer__bottom">
          <div className="footer__copyright">
            <p>
              <span>&copy; {currentYear} Griffin's World. All rights reserved.</span>
              <span aria-hidden="true">Made with ❤️ and lots of ☕</span>
            </p>
          </div>

          {/* Legal links with proper semantic structure */}
          <nav className="footer__legal" aria-label="Legal and utility links">
            <a href="#privacy" aria-label="Read our privacy policy">Privacy Policy</a>
            <a href="#terms" aria-label="Read our terms of service">Terms of Service</a>
            <a href="#sitemap" aria-label="View site map">Sitemap</a>
            <a href="#accessibility" aria-label="View accessibility statement">Accessibility</a>
          </nav>

          {/* Back to top button with enhanced accessibility */}
          <button 
            className="footer__scroll-top"
            onClick={scrollToTop}
            aria-label="Scroll to top of page"
            title="Back to top"
            type="button"
          >
            <span aria-hidden="true">↑</span>
            <span className="visually-hidden">Back to top</span>
          </button>
        </div>

        {/* Structured data for organization */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Griffin's World",
              "url": "https://griffins.world",
              "description": "Digital Innovation Hub showcasing modern web development and creative technology solutions",
              "foundingDate": "2024",
              "sameAs": socialLinks.map(link => link.url),
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "griffin@griffinsworld.dev",
                "contactType": "customer service"
              },
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "US"
              }
            })
          }}
        />
      </div>
    </footer>
  );
};

export default Footer; 